import { ReactNode, createContext, useMemo, useContext } from 'react';
import {
  posthog,
  type PostHog,
  type Properties,
  type CaptureOptions,
  type CaptureResult,
} from 'posthog-js';
import { PostHogProvider as BasePostHogProvider } from '@posthog/react';
import type { AnalyticsEvent } from '@fountain/waterworks-mixed-types/dist/commons/types/analytics';
import { sanitizePostHogEvent } from './PostHogSanitize';

const posthogToken = import.meta.env.VITE_POSTHOG_API_KEY;
if (posthogToken && posthogToken.trim()) {
  posthog.init(posthogToken, {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    // `defaults` is an PostHog-originated identifier for a bundle of settings
    // defined as-of a particular date, in our case the defaults as of 2025-11-30
    defaults: '2025-11-30',
    // only capture events after we call `identify`
    person_profiles: 'identified_only',
    // enables autocapturing $pageview events on SPAs by hooking into history
    // change events
    capture_pageview: 'history_change',
    capture_pageleave: false,
    autocapture: {
      // disable autocapturing DOM events
      dom_event_allowlist: [],
    },
    capture_exceptions: false,
    before_send: sanitizePostHogEvent,
    debug: import.meta.env.VITE_POSTHOG_DEBUG == 'true',
  });
} else {
  console.warn('PostHog token is not set');
}

export interface PostHogProxy extends PostHog {
  _rawClient: PostHog;
  _defaultProperties: Properties | undefined | null;

  capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined;

  captureException(
    error: unknown,
    additionalProperties?: Properties,
  ): CaptureResult | undefined;
}

export interface PostHogWithDefaults {
  posthog: PostHogProxy;
}

export const PostHogContext = createContext<PostHogWithDefaults>({
  posthog: makeProxy(posthog, {}),
});

export interface PostHogProviderProps {
  properties?: Properties;
  children: ReactNode;
}

export function PostHogRootProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const wrapped = useMemo(() => wrapPostHog(posthog, properties), [properties]);

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function PostHogPropertiesProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const { posthog } = useContext(PostHogContext);
  const wrapped = useMemo(
    () => wrapPostHog(posthog, properties),
    [posthog, properties],
  );

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function wrapPostHog(
  posthog: PostHog | PostHogProxy,
  properties?: Properties,
): PostHogProxy {
  if ((posthog as PostHogProxy)._rawClient !== undefined) {
    const cast = posthog as PostHogProxy;

    return makeProxy(cast._rawClient, {
      ...(cast._defaultProperties ?? {}),
      ...(properties ?? {}),
    });
  } else {
    return makeProxy(posthog, properties ?? {});
  }
}

function makeProxy(posthog: PostHog, defaults: Properties): PostHogProxy {
  function capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined {
    const mergedProperties =
      properties === null ? null : { ...defaults, ...(properties ?? {}) };
    return posthog.capture(name, mergedProperties, options);
  }

  function captureException(
    error: unknown,
    additionalProperties?: Properties,
  ): CaptureResult | undefined {
    return posthog.captureException(error, {
      ...defaults,
      ...(additionalProperties ?? {}),
    });
  }

  const handlers = {
    get(target: PostHog, prop: string | symbol, receiver: unknown) {
      if (prop === 'capture') {
        return capture;
      } else if (prop === 'captureException') {
        return captureException;
      } else if (prop === '_rawClient') {
        return target;
      } else if (prop === '_defaultProperties') {
        return defaults;
      }

      return Reflect.get(target, prop, receiver);
    },
  };

  return new Proxy(posthog, handlers) as PostHogProxy;
}
